import {
  useBoundProp,
  useCardSession,
  useCardSessionControls,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { useEffect, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button as ButtonPrimitive } from '@/components/ui/Button';
import {
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import type { KeyName } from '@/lib/key';
import { cn } from '@/lib/utils';

import { useElementId } from '../fieldIds';
import { Drain } from '../internals/Drain';
import { useHostHiddenActions } from '../internals/hostHiddenActions';
import { useHostIconActions } from '../internals/hostIconActions';
import { useHostTrailingActions } from '../internals/hostTrailingActions';
import { runningVerb } from '../internals/runningVerb';
import { useShortcutRunKey } from '../internals/shortcutRun';
import {
  BUTTON_ICON,
  BUTTON_TEXT_CLASS,
  BUTTON_VARIANT,
  ICON_CLASS,
  ICON_SM_CLASS,
  sizingClass,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

const QUIET_CLASS =
  'px-0 text-[length:var(--ac-meta)] text-foreground-muted hover:enabled:text-foreground hover:enabled:bg-transparent';

// A pill button, and any button once it starts running, is fully rounded
const PILL_SHAPE_CLASS = 'gap-[calc(6*var(--ac-u))] rounded-full';

// A label already written in running form ("Sending message…") carries its own
// ellipsis; runningVerb then falls back to it unchanged, so appending another
// one would read "Sending message……"
const ALREADY_RUNNING = /(…|\.\.\.)$/;

// The label cross-fades into the confirm text and back, keyed by which one is
// showing so the animation replays each time the button arms or reverts
const LABEL_SWAP_CLASS =
  'motion-opacity-in-0 motion-translate-y-in-[2px] motion-duration-[120ms] motion-reduce:motion-duration-[60ms]';

// The buttons a host that draws them as icons shows by their icon, with the key that presses them on the open card
// An acknowledge shows as a plain green tick, named for what it does rather than as "Got it": an update the user
// clears is read, and D clears it as it checks off any card
const ICON_ACTIONS: readonly {
  suffix: string;
  icon: (typeof Icons)[keyof typeof Icons];
  hotkey: readonly KeyName[];
  label?: string;
  done?: boolean;
}[] = [
  {
    suffix: '-acknowledge',
    icon: Icons.check,
    hotkey: ['d'],
    label: 'Mark as read',
    done: true,
  },
  {
    suffix: '-card_chat',
    icon: Icons.newChat,
    hotkey: ['c'],
  },
  {
    suffix: '-discard',
    icon: Icons.trash,
    hotkey: ['backspace'],
  },
  {
    suffix: '-dismiss',
    icon: Icons.trash,
    hotkey: ['backspace'],
  },
];

const RUN_HOTKEY: readonly KeyName[] = ['mod', 'enter'];

// The line between a split button's halves, drawn in the text colour so it reads
// on every variant
const SPLIT_DIVIDER_CLASS =
  'relative before:absolute before:inset-y-[calc(6*var(--ac-u))] before:left-0 before:w-px before:bg-current before:opacity-30';

// A ghost button with an icon, such as the header's Dismiss, shows only its icon and
// keeps the label as its accessible name. With `options`, a menu beside it picks the
// bound `value`, and while a draft the picked option names the button
export const Button: ComponentFn<typeof catalog, 'Button'> = ({
  props,
  emit,
  bindings,
}) => {
  const id = useElementId(props);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pressedInMenu = useRef(false);
  const session = useCardSession();
  const status = useCardStatus();
  const shortcutRunKey = useShortcutRunKey();
  const trailing = useHostTrailingActions();
  const hidden = useHostHiddenActions();
  const KeyTooltip = useHostIconActions();
  const { arm, disarm, validate, fieldChanged, commitNow } =
    useCardSessionControls();
  const armed = session.armed?.elementId === id ? session.armed : undefined;
  const choicePath = bindings?.value;
  const [choice, setChoice] = useBoundProp<string | boolean | undefined>(
    props.value,
    choicePath
  );
  const options = props.options ?? [];
  // Failed and finished cards keep the served label ("Retry", "Merged")
  const picked =
    status === 'draft' || status === 'running'
      ? options.find((option) => option.value === choice)
      : undefined;
  const label = picked?.label ?? props.label;
  const confirm = picked?.confirm ?? props.confirm;
  // A run keeps the method segment, held, so the control keeps its width
  const split =
    options.length > 0 &&
    choicePath !== undefined &&
    (status === 'draft' || status === 'running');
  const menu = split && status === 'draft' && props.busy !== true;

  // Anything that isn't the second press reverts an armed confirm: a scroll anywhere
  // (window sees it in the capture phase even for a nested scroll container), or a
  // click outside the button. A press of another element on the card already reverts
  // it through the session
  useEffect(() => {
    if (!armed) return;
    const revert = () => disarm(id);
    const revertOnOutsideClick = (event: MouseEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (!buttonRef.current?.contains(target)) revert();
    };
    window.addEventListener('scroll', revert, true);
    document.addEventListener('click', revertOnOutsideClick, true);
    return () => {
      window.removeEventListener('scroll', revert, true);
      document.removeEventListener('click', revertOnOutsideClick, true);
    };
  }, [armed, id, disarm]);

  const variant = props.variant ?? 'secondary';
  const iconAction = KeyTooltip
    ? ICON_ACTIONS.find((action) => id.endsWith(action.suffix))
    : undefined;
  const Icon =
    iconAction?.icon ?? (props.icon ? BUTTON_ICON[props.icon] : null);
  const iconOnly =
    Boolean(iconAction) || (variant === 'ghost' && Boolean(Icon));
  const shownVariant = iconAction?.done ? 'ghost' : variant;
  const iconLabel = iconAction?.label ?? props.label;
  // A split control keeps its draft corners through a run, so its halves still meet
  const pillShaped =
    props.appearance === 'pill' || (props.busy === true && !split);
  // A slow run keeps this label; the status line says it is still going
  const loadingText = props.busy ? busyLabel(label) : undefined;

  const primary =
    props.runAction === true || variant === 'primary' || confirm !== undefined;
  // Every option's label is reserved, so picking another keeps the button's width
  const restingLabels = [
    label,
    ...(split ? options.map((option) => option.label) : []),
    ...(props.reserveLabels ?? []),
  ];
  const reserveLabels = [
    ...new Set(
      [
        ...restingLabels,
        confirm,
        ...(split ? options.map((option) => option.confirm) : []),
        ...(primary ? restingLabels.map(busyLabel) : []),
      ].filter((text): text is string => text !== undefined)
    ),
  ];

  const pick = (value: string | boolean) => {
    if (value === choice) return;
    if (armed) disarm(id);
    setChoice(value);
    if (choicePath) fieldChanged(choicePath);
    commitNow();
  };

  const runsOnShortcut =
    shortcutRunKey !== undefined &&
    (props.runKey === shortcutRunKey ||
      (options.length > 0 && String(choice) === shortcutRunKey) ||
      (shortcutRunKey === 'acknowledge' && primary));
  const hotkey =
    iconAction?.hotkey ?? (runsOnShortcut ? RUN_HOTKEY : undefined);

  const pressable = (
    <ButtonPrimitive
      ref={buttonRef}
      id={id}
      data-card-primary={primary || undefined}
      data-key-hint={runsOnShortcut ? '⌘↵' : undefined}
      variant={BUTTON_VARIANT[shownVariant]}
      size={iconOnly ? 'xs' : 'sm'}
      iconOnly={iconOnly}
      isLoading={props.busy}
      loadingText={loadingText}
      disabled={props.disabled || session.readOnly}
      aria-label={iconOnly ? iconLabel : undefined}
      title={iconOnly && !hotkey ? iconLabel : undefined}
      className={cn(
        sizingClass(props.sizing) || 'shrink-0',
        BUTTON_TEXT_CLASS,
        shownVariant === 'primary' && 'text-accent-foreground',
        iconAction?.done && 'text-kind-done hover:enabled:text-kind-done',
        variant === 'danger' &&
          'bg-[var(--card-danger-tint)] text-danger-11 hover:enabled:bg-danger-4',
        variant === 'quiet' && QUIET_CLASS,
        pillShaped && PILL_SHAPE_CLASS,
        armed && 'relative isolate overflow-hidden',
        split && 'rounded-r-none'
      )}
      onClick={() => {
        if (primary && !validate()) return;
        if (confirm !== undefined && !armed) {
          arm(id, props.confirmHint);
          return;
        }
        emit('press');
      }}
    >
      {Icon ? <Icon className={ICON_CLASS} /> : null}
      {iconOnly ? null : (
        <span className="inline-grid leading-[var(--ac-button-lh)]">
          {reserveLabels.map((label) =>
            ALREADY_RUNNING.test(label) ? (
              // The same spinner and gap as the primitive's loading content, so the
              // button's own svg sizing applies to both
              <span
                key={label}
                aria-hidden="true"
                data-reserve-running=""
                className="invisible col-start-1 row-start-1 inline-flex items-center gap-[calc(8*var(--ac-u))]"
              >
                <Icons.loader className={ICON_SM_CLASS} />
                <ReservedLabel label={label} />
              </span>
            ) : (
              <ReservedLabel
                key={label}
                label={label}
                className="invisible col-start-1 row-start-1"
              />
            )
          )}
          <span
            key={armed ? 'confirm' : 'label'}
            className={cn('col-start-1 row-start-1', LABEL_SWAP_CLASS)}
          >
            {armed ? confirm : label}
          </span>
        </span>
      )}
      {armed ? (
        <Drain
          until={armed.until}
          className="inset-0 -z-10 rounded-none bg-transparent text-black opacity-30 dark:text-white"
        />
      ) : null}
    </ButtonPrimitive>
  );
  // A host that shows keys in tooltips names the button and its key on hover, in place of a hint drawn on it
  const button =
    KeyTooltip && hotkey ? (
      <KeyTooltip label={iconLabel} keys={hotkey}>
        {pressable}
      </KeyTooltip>
    ) : (
      pressable
    );

  // A button the host leaves out still hands on the host's own controls that follow it. A hidden Chat stays in the
  // DOM, unseen and out of reach, because the Feed's band styles find their row by it (feed.css)
  if (hidden.some((suffix) => id.endsWith(suffix)))
    return id.endsWith('-card_chat') ? (
      <>
        <button type="button" id={id} hidden aria-hidden="true" tabIndex={-1} />
        {trailing}
      </>
    ) : null;

  if (!split)
    // The host's own controls follow Chat, or Continue working, which stands in for it
    return trailing &&
      (id.endsWith('-card_chat') || id.endsWith('-continue_working')) ? (
      <>
        {button}
        {trailing}
      </>
    ) : (
      button
    );

  const segment = (
    <ButtonPrimitive
      variant={BUTTON_VARIANT[variant]}
      size="sm"
      aria-label={props.menuLabel ?? 'More options'}
      disabled={!menu || props.disabled || session.readOnly}
      className={cn(
        BUTTON_TEXT_CLASS,
        'shrink-0 rounded-l-none px-[calc(8*var(--ac-u))]',
        variant === 'danger' &&
          'bg-[var(--card-danger-tint)] text-danger-11 hover:enabled:bg-danger-4',
        SPLIT_DIVIDER_CLASS
      )}
    >
      <Icons.chevronDown className={ICON_CLASS} aria-hidden="true" />
    </ButtonPrimitive>
  );

  return (
    <div
      className={cn(
        'inline-flex items-stretch',
        sizingClass(props.sizing) || 'shrink-0'
      )}
    >
      {button}
      {menu ? (
        <DropdownMenuRoot
          onOpenChange={() => {
            pressedInMenu.current = false;
          }}
        >
          <DropdownMenuTrigger asChild>{segment}</DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            fit
            onPointerDown={() => {
              pressedInMenu.current = true;
            }}
          >
            {/* The menu keys its items by string; the bound value keeps its own type, such as a boolean */}
            <DropdownMenuRadioGroup
              value={choice === undefined ? undefined : String(choice)}
              onValueChange={(key) => {
                const option = options.find(
                  (candidate) => String(candidate.value) === key
                );
                if (option) pick(option.value);
              }}
            >
              {options.map((option) => (
                <DropdownMenuRadioItem
                  key={String(option.value)}
                  value={String(option.value)}
                  className="pr-8"
                  // The press that opened the menu can release over an item; only a
                  // press that began inside the menu picks
                  onPointerUp={(event) => {
                    if (!pressedInMenu.current) event.preventDefault();
                  }}
                >
                  <span className="line-clamp-2 break-words">
                    {option.label}
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      ) : (
        segment
      )}
    </div>
  );
};

const ReservedLabel = ({
  label,
  className,
}: {
  label: string;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    data-reserve={label}
    className={cn('before:content-[attr(data-reserve)]', className)}
  />
);

function busyLabel(label: string): string {
  const verb = runningVerb(label);
  return ALREADY_RUNNING.test(verb) ? verb : `${verb}…`;
}
